// src/save/panel.ts
/**
 * THE SAVE PANEL — the front door's "Your save" section (ADR-0070).
 *
 * Status ("saved · synced 2 min ago" / "local only"), the save key with Copy
 * and Copy link (a `?savekey=` link opens the same game on any deployment),
 * "Use another save key", Export / Import as the no-network fallback, and
 * Reset game behind an in-page confirm.
 *
 * DOM only; every decision is in `store.ts` / `cloud.ts`.
 */
import { formatSaveKey, sanitizeBlob, withoutLocalSlots, type SaveBlob } from "./blob.js";
import type { CloudSync, SyncStatus } from "./cloud.js";
import type { SaveStore } from "./store.js";

/** "just now", "2 min ago", "3 h ago", "4 days ago". Pure. */
export function agoText(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${String(m)} min ago`;
  const h = Math.round(m / 60);
  if (h < 36) return `${String(h)} h ago`;
  return `${String(Math.round(h / 24))} days ago`;
}

/** The one-line status. Pure. */
export function statusText(s: SyncStatus, now: number): string {
  if (s.state === "stale") return "This save was reset or switched in another tab — reload";
  if (s.state === "local-only") return "Saved on this device · cloud sync unavailable here";
  if (s.state === "offline") return "Saved on this device · offline, will sync later";
  if (s.state === "syncing" || s.state === "idle") return "Saved · syncing…";
  if (s.unsynced) return "Saved · changes waiting to sync";
  return s.syncedAt === null ? "Saved · synced" : `Saved · synced ${agoText(now - s.syncedAt)}`;
}

function button(label: string, action: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "save-btn";
  b.dataset["saveAction"] = action;
  b.textContent = label;
  return b;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false; // no clipboard permission: the key is on screen to copy by hand
  }
}

export interface SavePanelDeps {
  readonly store: SaveStore;
  readonly cloud: CloudSync;
  readonly now?: () => number;
  readonly origin?: string;
}

/** How many game slots hold something (device settings excluded). */
function rememberedCount(store: SaveStore): number {
  return Object.values(withoutLocalSlots(store.snapshot()).slots).filter((s) => s.data !== null)
    .length;
}

export function mountSavePanel(host: HTMLElement, deps: SavePanelDeps): HTMLElement {
  const { store, cloud } = deps;
  const now = deps.now ?? Date.now;
  const origin = deps.origin ?? location.origin;

  const root = document.createElement("details");
  root.className = "save-panel";
  root.id = "save-panel";
  const summary = document.createElement("summary");
  const title = document.createElement("span");
  title.className = "save-title";
  title.textContent = "Your save";
  const status = document.createElement("span");
  status.className = "save-status";
  status.dataset["saveStatus"] = "";
  summary.append(title, status);

  const body = document.createElement("div");
  body.className = "save-body";
  const counts = document.createElement("p");
  counts.className = "save-summary";
  const keyLabel = document.createElement("p");
  keyLabel.className = "save-label";
  keyLabel.textContent = "Save key — use it on any Weftbound site to continue this game";
  const keyEl = document.createElement("code");
  keyEl.className = "save-key";
  keyEl.dataset["saveKey"] = "";

  const copyRow = document.createElement("div");
  copyRow.className = "save-row";
  const copyKey = button("Copy key", "copy-key");
  const copyLink = button("Copy link", "copy-link");
  copyRow.append(copyKey, copyLink);

  const adopt = document.createElement("form");
  adopt.className = "save-adopt";
  const input = document.createElement("input");
  input.type = "text";
  input.name = "savekey";
  input.placeholder = "Paste another save key";
  input.autocomplete = "off";
  input.spellcheck = false;
  input.setAttribute("aria-label", "Another save key");
  const use = button("Use this save", "adopt");
  use.type = "submit";
  adopt.append(input, use);

  const fileRow = document.createElement("div");
  fileRow.className = "save-row";
  const exportBtn = button("Export file", "export");
  const importBtn = button("Import file", "import");
  const file = document.createElement("input");
  file.type = "file";
  file.accept = "application/json,.json";
  file.hidden = true;
  fileRow.append(exportBtn, importBtn, file);

  const resetRow = document.createElement("div");
  resetRow.className = "save-row save-danger";
  const resetBtn = button("Reset game…", "reset");
  resetRow.append(resetBtn);
  const confirm = document.createElement("div");
  confirm.className = "save-confirm";
  confirm.hidden = true;
  const ask = document.createElement("p");
  ask.textContent =
    "Forget everything Rúna has done — places, mends, quests, her bag and her level — on this device and in the cloud? This cannot be undone.";
  const yes = button("Yes, reset", "reset-confirm");
  const no = button("Keep my game", "reset-cancel");
  confirm.append(ask, yes, no);

  const note = document.createElement("p");
  note.className = "save-note";
  note.setAttribute("aria-live", "polite");
  note.dataset["saveNote"] = "";

  body.append(counts, keyLabel, keyEl, copyRow, adopt, fileRow, resetRow, confirm, note);
  root.append(summary, body);
  host.append(root);

  const say = (text: string): void => {
    note.textContent = text;
  };
  const refresh = (): void => {
    const s = cloud.status();
    status.textContent = statusText(s, now());
    root.dataset["saveState"] = s.state;
    const n = rememberedCount(store);
    const updated = store.snapshot().updatedAt;
    counts.textContent =
      n === 0
        ? "Nothing remembered yet — a fresh game."
        : `${String(n)} part${n === 1 ? "" : "s"} of her journey remembered${updated > 0 ? ` · last change ${agoText(now() - updated)}` : ""}`;
    keyEl.textContent = formatSaveKey(cloud.key());
  };

  const link = (): string => `${origin}/?savekey=${cloud.key()}`;
  copyKey.addEventListener("click", () => {
    void copyText(cloud.key()).then((ok) => {
      say(ok ? "Save key copied." : "Copy the key above by hand.");
    });
  });
  copyLink.addEventListener("click", () => {
    void copyText(link()).then((ok) => {
      say(ok ? "Link copied — open it on another Weftbound site to continue there." : link());
    });
  });
  adopt.addEventListener("submit", (e) => {
    e.preventDefault();
    say("Looking for that save…");
    void cloud.adoptKey(input.value).then((err) => {
      if (err === null) input.value = "";
      say(err ?? "Now playing that save.");
      refresh();
    });
  });
  exportBtn.addEventListener("click", () => {
    const blob = new Blob([JSON.stringify(withoutLocalSlots(store.snapshot()), null, 1)], {
      type: "application/json",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `weftbound-save-${new Date(now()).toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(a.href);
    }, 1000);
    say("Save exported.");
  });
  importBtn.addEventListener("click", () => {
    file.click();
  });
  file.addEventListener("change", () => {
    const f = file.files?.[0];
    file.value = "";
    if (f === undefined) return;
    void f.text().then((text) => {
      let b: SaveBlob | null = null;
      try {
        b = sanitizeBlob(JSON.parse(text) as unknown);
      } catch {
        b = null;
      }
      if (b === null) {
        say("That file is not a Weftbound save.");
        return;
      }
      // A deliberate replace: stamp it as a reset so the cloud copy is replaced, not merged.
      store.adopt({ ...b, resetAt: Math.max(now(), store.snapshot().resetAt + 1) });
      void cloud.push();
      say("Save imported.");
      refresh();
    });
  });
  resetBtn.addEventListener("click", () => {
    confirm.hidden = false;
    resetRow.hidden = true;
    no.focus();
  });
  no.addEventListener("click", () => {
    confirm.hidden = true;
    resetRow.hidden = false;
  });
  yes.addEventListener("click", () => {
    confirm.hidden = true;
    resetRow.hidden = false;
    void cloud.resetGame().then(() => {
      say("A fresh game. Reload any other open Weftbound tabs.");
      refresh();
    });
  });

  cloud.onStatus(refresh);
  store.on(refresh);
  setInterval(refresh, 30_000);
  refresh();
  return root;
}
